import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DEFAULT_PAGE_SIZE } from 'core/slices/tasks';
import { ADJUSTMENT_TASK, ZERO_STOCK_TASK, resolveLegacyTaskType } from 'core/slices/utils/constants';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import TasksDetailsContainer from '../../../redux/containers/tasks/details/TasksDetails';
import ExcelDialogContainer from '../../../redux/containers/tasks/excel/ExcelDialog';
import BaseDrawer from '../../templates/BaseDrawer';
import getDescriptionCard from '../cardDescription/displayDescription';
import TasksFilters from '../filters/TasksFilters';
import TasksHeader from '../header/TasksHeader';
import { useSearchState } from '../hooks/useSearchState';
import { useTasksReload } from '../hooks/useTasksReload';
import { countActiveFilters, countNarrowingFilters, DONE_STATUSES } from '../taskUtils';

const EXCEL_TASK_TYPES = [ADJUSTMENT_TASK, ZERO_STOCK_TASK];

// ── Type badge ────────────────────────────────────────────────────────────────

function TypeBadge({ resource, typeLabel, hexColor, size = 'small' }) {
  const isLarge = size === 'large';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: isLarge ? 0.4 : 0.25,
        borderRadius: 1,
        bgcolor: hexColor ? alpha(hexColor, 0.12) : (theme => theme.bruno.colors.inactive),
      }}
    >
      {resource?.material_icon && (
        <span
          className="material-icons"
          style={{ fontSize: isLarge ? 14 : 12, color: hexColor || 'inherit', lineHeight: 1 }}
        >
          {resource.material_icon}
        </span>
      )}
      <Typography
        variant="caption"
        sx={{
          fontSize: isLarge ? '0.68rem' : '0.6rem',
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          color: hexColor || 'text.secondary',
        }}
      >
        {typeLabel}
      </Typography>
    </Box>
  );
}

// ── Swipeable task row (mobile only) ─────────────────────────────────────────

const BUTTON_WIDTH = 72;  // width of each action button
const REVEAL_WIDTH = BUTTON_WIDTH * 2;  // total reveal: Error + Done

function SwipeableTaskRow({ children, onError, onDone, disabled, errorLabel = 'Error', doneLabel = 'Done', isOpen, onOpenChange, onRegister, onUnregister, updatingStatus = null }) {
  const slideRef = useRef(null);
  const startXRef = useRef(null);
  const currentOffsetRef = useRef(0);
  const isOpenRef = useRef(isOpen ?? false);

  const setTranslate = (x, animate) => {
    const el = slideRef.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.18s ease-out' : 'none';
    el.style.transform = `translateX(${x}px)`;
    currentOffsetRef.current = x;
  };

  // Register close() with parent so it can call it directly without React re-render.
  // Empty deps [] is intentional: we only register once at mount. onRegister/onUnregister
  // are stable arrow functions defined inline in the parent list render, so capturing them
  // at mount is correct — re-registering on every render would thrash the registry.
  useLayoutEffect(() => {
    const close = () => { isOpenRef.current = false; setTranslate(0, true); };
    onRegister?.(close);
    return () => onUnregister?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external isOpen changes directly to DOM — no React render delay
  useLayoutEffect(() => {
    const wasOpen = isOpenRef.current;
    const nowOpen = isOpen ?? false;
    isOpenRef.current = nowOpen;
    if (wasOpen !== nowOpen) {
      setTranslate(nowOpen ? -REVEAL_WIDTH : 0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startYRef = useRef(null);
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);
  const disabledRef = useRef(disabled);
  const isUpdatingRef = useRef(false);
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => { disabledRef.current = disabled; }, [disabled]);
  useEffect(() => { onOpenChangeRef.current = onOpenChange; }, [onOpenChange]);

  const handlePointerUp = useCallback(() => {
    if (startXRef.current === null) return;
    startXRef.current = null;
    startYRef.current = null;
    if (containerRef.current) containerRef.current.style.touchAction = 'pan-y';
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    const shouldOpen = currentOffsetRef.current <= -(REVEAL_WIDTH * 0.5);
    setTranslate(shouldOpen ? -REVEAL_WIDTH : 0, true);
    if (shouldOpen !== isOpenRef.current) {
      isOpenRef.current = shouldOpen;
      onOpenChangeRef.current?.(shouldOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register pointer listeners directly on the DOM node with { passive: false } so
  // preventDefault() is honoured on iOS Safari — React synthetic events and the
  // default addEventListener passive flag prevent preventDefault from working in
  // scroll containers on iOS, causing the browser to ignore the swipe gesture.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onDown = (e) => {
      if (disabledRef.current || isUpdatingRef.current) return;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      isDraggingRef.current = false;
    };

    const onMove = (e) => {
      if (disabledRef.current || isUpdatingRef.current) return;
      if (startXRef.current === null) return;
      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;

      if (!isDraggingRef.current) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          startXRef.current = null;
          return;
        }
        isDraggingRef.current = true;
        // Capture only after confirming horizontal swipe — capturing on pointerdown
        // inhibits native vertical scroll on mobile.
        try { el.setPointerCapture(e.pointerId); } catch (_) { }
        el.style.touchAction = 'none';
      }

      e.preventDefault();
      const base = isOpenRef.current ? -REVEAL_WIDTH : 0;
      const next = Math.min(0, Math.max(base + dx, -REVEAL_WIDTH));
      setTranslate(next, false);
    };

    el.addEventListener('pointerdown', onDown, { passive: false });
    el.addEventListener('pointermove', onMove, { passive: false });
    el.addEventListener('pointerup', handlePointerUp);
    el.addEventListener('pointerleave', handlePointerUp);
    el.addEventListener('pointercancel', handlePointerUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', handlePointerUp);
      el.removeEventListener('pointerleave', handlePointerUp);
      el.removeEventListener('pointercancel', handlePointerUp);
    };
    // handlePointerUp is stable (useCallback with stable dep); re-register only when it changes
  }, [handlePointerUp]);

  const handleClose = () => onOpenChange?.(false);
  const isUpdating = updatingStatus !== null;
  useEffect(() => { isUpdatingRef.current = isUpdating; }, [isUpdating]);
  // Keep swipe open while updating so the user sees the spinner on the chosen button
  const handleAction = (fn) => {
    if (isUpdating) return;
    fn?.();
  };

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', overflow: 'hidden', touchAction: 'pan-y' }}
    >
      {/* Action buttons revealed on swipe left */}
      <Box sx={{
        position: 'absolute', right: 0, top: 0, bottom: 0,
        width: REVEAL_WIDTH,
        display: 'flex',
      }}>
        <Box
          onClick={() => handleAction(onError)}
          sx={{
            width: BUTTON_WIDTH, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 0.25,
            bgcolor: '#ffebee',
            cursor: isUpdating ? 'default' : 'pointer',
            opacity: isUpdating && updatingStatus !== 'error' ? 0.4 : 1,
            '&:active': isUpdating ? {} : { bgcolor: '#ffcdd2' },
          }}
        >
          {updatingStatus === 'error' ? (
            <CircularProgress size={22} sx={{ color: '#e53935' }} />
          ) : (
            <span className="material-icons" style={{ fontSize: 22, color: '#e53935' }}>error</span>
          )}
          <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#e53935', letterSpacing: 0.3 }}>
            {errorLabel}
          </Box>
        </Box>

        <Box
          onClick={() => handleAction(onDone)}
          sx={{
            width: BUTTON_WIDTH, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 0.25,
            bgcolor: '#e8f5e9',
            cursor: isUpdating ? 'default' : 'pointer',
            opacity: isUpdating && updatingStatus !== 'done' ? 0.4 : 1,
            '&:active': isUpdating ? {} : { bgcolor: '#c8e6c9' },
          }}
        >
          {updatingStatus === 'done' ? (
            <CircularProgress size={22} sx={{ color: '#43a047' }} />
          ) : (
            <span className="material-icons" style={{ fontSize: 22, color: '#43a047' }}>check_circle</span>
          )}
          <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#43a047', letterSpacing: 0.3 }}>
            {doneLabel}
          </Box>
        </Box>
      </Box>

      {/* Sliding content */}
      <Box
        ref={slideRef}
        sx={{ bgcolor: 'background.paper', position: 'relative', zIndex: 1 }}
        onClick={isOpen && !isUpdating ? handleClose : undefined}
      >
        {children}
      </Box>
    </Box>
  );
}

// ── Task row ──────────────────────────────────────────────────────────────────

function TaskRow({ task, t, resource, brandDict, onClick, completed }) {
  const typeLabel = t(`bruno.tasks_names.${resolveLegacyTaskType(task.task_type)}`, task.task_type || '');

  const hexColor = resource?.hex_color
    ? (resource.hex_color.startsWith('#') ? resource.hex_color : `#${resource.hex_color}`)
    : null;

  return (
    <ListItem
      disablePadding
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.5,
        alignItems: 'flex-start',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.15s',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
      }}
    >
      {/* Colored type indicator bar */}
      <Box sx={{
        width: 4,
        borderRadius: 4,
        alignSelf: 'stretch',
        flexShrink: 0,
        mr: 1.5,
        bgcolor: hexColor ? alpha(hexColor, 0.5) : 'grey.200',
      }} />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {(() => {
          const DescriptionCard = getDescriptionCard(task.task_type);
          return <DescriptionCard task={task} t={t} brandDict={brandDict} completed={completed} />;
        })()}

        {/* Mobile: badge bottom right */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', mt: 0.75 }}>
          <TypeBadge resource={resource} typeLabel={typeLabel} hexColor={hexColor} size="small" />
        </Box>
      </Box>

      {/* Desktop: badge + chevron on the right */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignSelf: 'center', ml: 2, flexShrink: 0, alignItems: 'center', gap: 1 }}>
        <TypeBadge resource={resource} typeLabel={typeLabel} hexColor={hexColor} size="large" />
        {onClick && <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
      </Box>

      {/* Mobile: chevron on the right */}
      {onClick && (
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignSelf: 'center', ml: 1, flexShrink: 0 }}>
          <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Box>
      )}
    </ListItem>
  );
}

// ── Skeleton row ──────────────────────────────────────────────────────────────

function TaskRowSkeleton() {
  return (
    <ListItem disablePadding sx={{ px: 3, py: 2, alignItems: 'flex-start' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width="55%" height={24} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="40%" height={18} />
        <Skeleton variant="text" width="30%" height={18} />
        <Skeleton variant="text" width="25%" height={18} />
      </Box>
      <Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: 6, flexShrink: 0, mt: 0.25, ml: 2 }} />
    </ListItem>
  );
}

// ── Generic selector modal ────────────────────────────────────────────────────

function SelectorModal({ open, onClose, title, searchPlaceholder, items, loading, currentValue, getKey, getLabel, onSelect, progress }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const handleSelect = (item) => {
    const p = progress?.[getKey(item)];
    if (p && p.total === 0) return;
    onSelect(item);
    handleClose();
  };

  const filtered = (items || []).filter((item) =>
    !search || getLabel(item).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <BaseDrawer open={open} onClose={handleClose} height="75vh" maxWidth={600}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
        <CategoryOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Box sx={{ px: 2, pt: 1.5, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', px: 1, pb: 2 }}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
            <ListItem key={i} disablePadding sx={{ px: 1, py: 0.5 }}>
              <Skeleton variant="text" width="100%" height={44} />
            </ListItem>
          ))
          : filtered.length === 0
            ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography variant="body2" color="text.disabled">
                  {t('bruno.tasks.no_result_found', 'No results')}
                </Typography>
              </Box>
            )
            : filtered.map((item) => {
              const p = progress?.[getKey(item)];
              const disabled = progress ? p?.total === 0 : false;
              return (
                <ListItem key={getKey(item) ?? 'all'} disablePadding>
                  <ListItemButton
                    selected={getKey(item) === currentValue}
                    disabled={disabled}
                    onClick={() => handleSelect(item)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText primary={getLabel(item)} />
                  </ListItemButton>
                </ListItem>
              );
            })}
      </List>
    </BaseDrawer>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function TasksView({
  tasksFilterList,
  tasksFilterSearchList,
  tasksList,
  progressTasksType,
  progressTasksTypeBaseline,
  taskLoading,
  loadingProgress,
  moreTasksLoading,
  currentTaskType,
  resourcesTasksType,
  filters,
  supplierSelected,
  supplierIds,
  currentStore,
  aislesByTaskList,
  aisleClientNames,
  loadingAisles,
  currentStoreName,
  currentChain,
  brandDict,
  tasksStatusList,
  hasOverheadTasks,
  progressLoadedStore,
  getTaskList,
  clearTaskList,
  loadMoreTasks,
  loadTaskList,
  getTasksProgress,
  getAislesByTasks,
  getResourcesOverhead,
  getResourcesTaskDetailsFeatures,
  setCurrentTaskType,
  addFilter,
  resetFilter,
  setSupplierSelected,
  setTasksFilter,
  getAllBrands,
  getTasksStatus,
  updateTasks,
  updateTaskStatusLocally,
  getCategoriesData,
  getCategoryProgress,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [taskTypeOpen, setTaskTypeOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const [updatingTask, setUpdatingTask] = useState(null); // { taskId, status }
  const swipeCloseRegistry = useRef({});

  const handleSwipeOpen = useCallback((taskId) => {
    const strId = String(taskId);
    // Close previously open row immediately via DOM — no React re-render needed
    Object.entries(swipeCloseRegistry.current).forEach(([id, closeFn]) => {
      if (id !== strId) closeFn();
    });
    // Defer state update so it doesn't block the pointerup handler
    setTimeout(() => setOpenSwipeId(strId), 0);
  }, []);

  const handleSwipeClose = useCallback(() => setTimeout(() => setOpenSwipeId(null), 0), []);

  const prevListLengthRef = useRef(0);
  const scrollContainerRef = useRef(null);
  const loadingRef = useRef(false);
  const taskLoadingStartedRef = useRef(false);

  // Any caller that forces a page-0 reload outside the normal store/type/filter
  // reload (useTasksReload) must reset this local pagination state in the same
  // tick. getTaskList(0) always replaces tasksFilterList with just that page's
  // results — leaving stale page/hasMore around after such a call caps the list
  // at page 0 and can leave hasMore=false from a previous full scroll, blocking
  // further loads until the view is unmounted and remounted.
  const resetPagination = useCallback(() => {
    setPage(0);
    setHasMore(true);
    prevListLengthRef.current = 0;
    loadingRef.current = false;
    taskLoadingStartedRef.current = false;
  }, []);

  // Always points to the latest context so async callbacks reading it after an
  // await see the CURRENT value, not the one captured in their own closure.
  const contextRef = useRef({ store: currentStore, taskType: currentTaskType });
  contextRef.current = { store: currentStore, taskType: currentTaskType };

  const { search, searching, handleSearch, resetSearch } = useSearchState(tasksFilterSearchList, tasksList, setTasksFilter);

  // On mount or store change: reload list, set task type from URL param, clear stale state.
  // prevStoreRef and the typeParam logic are merged into one effect so they can't race —
  // a separate prevStoreRef effect running after this one would overwrite the typeParam set here.
  const prevStoreRef = useRef(currentStore);
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const storeChanged = prevStoreRef.current !== currentStore;
    const progressStale = progressLoadedStore !== currentStore;

    if (progressStale) {
      clearTaskList();
      loadTaskList();
      resetFilter();
    }

    if (typeParam) {
      setCurrentTaskType(typeParam);
    } else if (storeChanged || (progressLoadedStore && progressStale)) {
      setCurrentTaskType('');
    }

    if (storeChanged) setSelectedTask(null);

    prevStoreRef.current = currentStore;
  }, [currentStore, progressLoadedStore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load progress + categories when landing directly on /tasks (e.g. page reload).
  useEffect(() => {
    if (currentStore && progressLoadedStore !== currentStore) {
      // Only while no task type is selected: once there is one, useTasksReload
      // owns progress and fetches it with the active filters. An unfiltered
      // call here would race it on the shared TASKS_PROGRESS abort key.
      if (!currentTaskType) getTasksProgress();
      getCategoriesData();
      getCategoryProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  // Close the details modal when the task type changes within the same store.
  useEffect(() => {
    setSelectedTask(null);
  }, [currentTaskType]);

  // Filters live in the global filter slice, so leaving /tasks would otherwise
  // carry them into other views (Home's getTasksProgress would keep reporting
  // filtered counts). React runs this cleanup before the next route's mount
  // effects, so their fetches already see the reset filters.
  useEffect(() => {
    return () => resetFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback for re-entry via sidebar: if currentTaskType is not available in
  // the current store's resources (user switched store on Home then navigated
  // to /tasks — TasksView was unmounted so the effect above missed the change),
  // reset it once resources load.
  // Skip if the type came from the URL param — the URL is authoritative and the
  // effect in this case will set the correct type via setCurrentTaskType(typeParam).
  const urlTaskType = searchParams.get('type');
  useEffect(() => {
    if (!currentTaskType || !resourcesTasksType?.length) return;
    if (progressLoadedStore !== currentStore) return;
    if (urlTaskType) return;
    const exists = resourcesTasksType.some(r => r.task_type === currentTaskType);
    if (!exists) setCurrentTaskType('');
  }, [currentTaskType, resourcesTasksType, progressLoadedStore, currentStore, urlTaskType, setCurrentTaskType]);

  // Reset + reload task list when store / type / filters change
  useTasksReload({
    currentStore,
    currentTaskType,
    filters,
    clearTaskList,
    loadTaskList,
    getAislesByTasks,
    getResourcesOverhead,
    getResourcesTaskDetailsFeatures,
    getTaskList,
    getTasksProgress,
    getAllBrands,
    getTasksStatus,
    onReloadStart: () => {
      resetPagination();
      resetSearch();
    },
  });

  // Detect end of results: when a load finishes, check if we got a short batch
  // (< PAGE_SIZE means the server has no more items).
  useEffect(() => {
    if (moreTasksLoading || taskLoading) {
      taskLoadingStartedRef.current = true;
      return;
    }
    if (!taskLoadingStartedRef.current) return;
    taskLoadingStartedRef.current = false;

    const len = (tasksFilterList || []).length;
    const grew = len - prevListLengthRef.current;
    // Short batch = last page from server
    if (grew >= 0 && grew < DEFAULT_PAGE_SIZE) {
      setHasMore(false);
    }
    prevListLengthRef.current = len;
    loadingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksFilterList, moreTasksLoading, taskLoading]);

  const handleLoadMore = useCallback(() => {
    if (searching || loadingRef.current || !hasMore || taskLoading || moreTasksLoading) return;

    // Guard: if the full list is known, stop when paginated list has reached it
    // (mirrors mobile's tasksList.length === pendingTasks check)
    const currentLen = (tasksFilterList || []).length;
    const fullLen = (tasksFilterSearchList || []).length;
    if (fullLen > 0 && currentLen >= fullLen) {
      setHasMore(false);
      return;
    }

    loadingRef.current = true;
    const next = page + 1;
    setPage(next);
    loadMoreTasks();
    getTaskList(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, hasMore, page, taskLoading, moreTasksLoading, tasksFilterList, tasksFilterSearchList]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) handleLoadMore();
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [handleLoadMore]);

  const resourceMap = useMemo(() => {
    if (!resourcesTasksType) return {};
    return resourcesTasksType.reduce((acc, r) => { acc[r.task_type] = r; return acc; }, {});
  }, [resourcesTasksType]);

  const handleSwipeAction = useCallback(async (task, status) => {
    // Capture context at start vs. read it LIVE after await. Using the callback's
    // closure for the post-await read would always see the same values it was
    // created with — a context change while updateTasks is pending would be
    // invisible, and we'd refresh lists belonging to a view the user left.
    const keyAtStart = `${contextRef.current.store}|${contextRef.current.taskType}`;
    const contextChanged = () =>
      keyAtStart !== `${contextRef.current.store}|${contextRef.current.taskType}`;

    // Mark this specific task+button as updating so the row shows a spinner
    // on the chosen action. List remains intact — no full-list skeleton.
    setUpdatingTask({ taskId: task.task_id, status });
    try {
      // Slice handles its own success/error snackbars and returns { success }.
      const result = await updateTasks(
        task.task_id, task.task_type, status, undefined, task.creation_timestamp,
      );
      if (contextChanged()) return;
      // Refresh on success, and on a stale task, whose row now holds a newer
      // task. On another error nothing changed server-side, the snackbar
      // already told the user, and the row stays in place for retry.
      if (result?.success) {
        updateTaskStatusLocally(task.task_id, status);
      }
      if (result?.success || result?.staleTask) {
        resetPagination();
        getTaskList(0);
        getTasksProgress();
        getAislesByTasks();
      }
    } finally {
      if (!contextChanged()) setUpdatingTask(null);
    }
  }, [currentStore, currentTaskType, updateTasks, updateTaskStatusLocally, getTaskList, getTasksProgress, getAislesByTasks, resetPagination]);


  const activeCount = countActiveFilters(filters, supplierSelected);
  // Suppress full-list skeleton while a per-row update is in flight — the row
  // itself shows a spinner on the chosen button. Blanking the whole list at
  // that moment would hide the spinner feedback.
  const isLoading = (taskLoading || loadingProgress) && !updatingTask;

  const typeProgress = currentTaskType && progressTasksType ? progressTasksType[currentTaskType] : null;

  const pendingCount = typeProgress?.pending ?? 0;
  const completedCount = (typeProgress?.total ?? 0) - (typeProgress?.pending ?? 0);
  const showAsCompleted = !!filters?.completed;

  // One source for the counter. getTasksProgress forwards the same filters the
  // list receives, so these totals already describe the rows below them. The
  // per-aisle counts are not an alternative source: /aisles does not know about
  // every filter, so summing them reported a wider universe than the list.
  const counterValue = searching
    ? (tasksFilterList || []).length
    : showAsCompleted
      ? completedCount
      : pendingCount;

  // Baseline totals ignore the narrowing filters, so the header can show
  // filtered/total while any of them is active. Search already shows the
  // match count, so it keeps the plain counter.
  const baselineProgress = currentTaskType && progressTasksTypeBaseline
    ? progressTasksTypeBaseline[currentTaskType]
    : null;
  const baselinePending = baselineProgress?.pending ?? 0;
  const baselineCompleted = (baselineProgress?.total ?? 0) - (baselineProgress?.pending ?? 0);
  const counterTotal = !searching && baselineProgress && countNarrowingFilters(filters, supplierSelected, currentTaskType) > 0
    ? (showAsCompleted ? baselineCompleted : baselinePending)
    : null;

  const taskTypeName = currentTaskType
    ? t(`bruno.tasks_names.${resolveLegacyTaskType(currentTaskType)}`, currentTaskType)
    : null;

  const handleApplyFilters = ({ supplier, completed, priority, overhead, aisle, spread_to_fill }) => {
    if (supplier?.id !== supplierSelected?.id) {
      setSupplierSelected(supplier);
      addFilter('supplier_id', supplier?.id && supplier.id !== 'all' ? supplier.id : null);
    }
    // Only call addFilter when value actually changes — addFilter toggles/deletes
    // when current === new, which would incorrectly remove keys from filters state
    const currentCompleted = !!(filters?.completed);
    const currentPriority = !!(filters?.priority);
    const currentOverhead = !!(filters?.overhead);
    const currentAisle = filters?.aisle || null;
    if (currentCompleted !== !!completed) addFilter('completed', !!completed);
    if (currentPriority !== !!priority) addFilter('priority', !!priority);
    if (currentOverhead !== !!overhead) addFilter('overhead', overhead ? true : null);
    if (currentAisle !== (aisle || null)) addFilter('aisle', aisle || null);
    if (spread_to_fill !== undefined) {
      const currentSpread = filters?.spread_to_fill ?? null;
      if (currentSpread !== spread_to_fill) addFilter('spread_to_fill', spread_to_fill);
    }
    setFiltersOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2 }}>

      {/* Header (responsive) */}
      <TasksHeader
        isMobile={isMobile}
        currentTaskType={currentTaskType}
        taskTypeName={taskTypeName}
        activeCount={activeCount}
        isLoading={isLoading}
        showAsCompleted={showAsCompleted}
        counterValue={counterValue}
        counterTotal={counterTotal}
        search={search}
        onSearch={handleSearch}
        onFiltersOpen={() => setFiltersOpen(true)}
        onTaskTypeOpen={() => setTaskTypeOpen(true)}
        onExcelOpen={() => setExcelOpen(true)}
        excelTaskTypes={EXCEL_TASK_TYPES}
        currentStoreName={currentStoreName}
        currentChain={currentChain}
        t={t}
      />

      {/* ── Task list (scrollable) ───────────────────────────────────────── */}
      <Box ref={scrollContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {!currentTaskType ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 1 }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: 4, bgcolor: theme => theme.bruno.colors.inactive,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1,
            }}>
              <Box component="span" className="material-icons" sx={{ fontSize: 30, color: 'text.disabled' }}>checklist</Box>
            </Box>
            <Typography variant="body1" color="text.disabled" textAlign="center">
              {t('bruno.tasks.select_task_type', 'Select a task type')}
            </Typography>
          </Box>
        ) : isLoading ? (
          <List disablePadding>
            {[...Array(8)].map((_, i) => (
              <React.Fragment key={i}>
                <TaskRowSkeleton />
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (tasksFilterList || []).length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 1.5 }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: 4, bgcolor: theme => theme.bruno.colors.inactive,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1,
            }}>
              <Box component="span" className="material-icons" sx={{ fontSize: 30, color: 'text.disabled' }}>
                search_off
              </Box>
            </Box>
            <Typography variant="body1" color="text.disabled" textAlign="center">
              {t('bruno.tasks.no_tasks_found', 'No tasks found for the selected filters')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {(tasksFilterList || []).map((task) => (
              <React.Fragment key={task.task_id}>
                {isMobile && !showAsCompleted ? (
                  <SwipeableTaskRow
                    disabled={false}
                    onError={() => handleSwipeAction(task, 'error')}
                    onDone={() => handleSwipeAction(task, 'done')}
                    errorLabel={t('bruno.buttons.swipe_error', 'Error')}
                    doneLabel={t('bruno.buttons.swipe_done', 'Done')}
                    isOpen={openSwipeId === String(task.task_id)}
                    onOpenChange={(val) => val ? handleSwipeOpen(task.task_id) : handleSwipeClose()}
                    onRegister={(closeFn) => { swipeCloseRegistry.current[String(task.task_id)] = closeFn; }}
                    onUnregister={() => { delete swipeCloseRegistry.current[String(task.task_id)]; }}
                    updatingStatus={updatingTask?.taskId === task.task_id ? updatingTask.status : null}
                  >
                    <TaskRow task={task} t={t} resource={resourceMap[task.task_type]} brandDict={brandDict} onClick={() => setSelectedTask(task)} completed={DONE_STATUSES.has(task.status)} />
                  </SwipeableTaskRow>
                ) : (
                  <TaskRow task={task} t={t} resource={resourceMap[task.task_type]} brandDict={brandDict} onClick={() => setSelectedTask(task)} completed={showAsCompleted} />
                )}
                <Divider />
              </React.Fragment>
            ))}

            {!searching && (
              <Box sx={{ py: 2.5, textAlign: 'center' }}>
                {moreTasksLoading ? (
                  <CircularProgress size={22} thickness={4} />
                ) : hasMore ? (
                  <Box sx={{ height: 1 }} />
                ) : (
                  <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: 2, textTransform: 'uppercase' }}>
                    {t('bruno.tasks.list_end', 'There are no more tasks')}
                  </Typography>
                )}
              </Box>
            )}
          </List>
        )}
      </Box>

      {/* ── Task type selector modal ──────────────────────────────────────── */}
      <SelectorModal
        open={taskTypeOpen}
        onClose={() => setTaskTypeOpen(false)}
        title={t('bruno.drawer.tasks', 'Tasks')}
        searchPlaceholder={t('bruno.tasks.search', 'Search')}
        items={resourcesTasksType}
        loading={(loadingProgress || progressLoadedStore !== currentStore) && !resourcesTasksType?.length}
        currentValue={currentTaskType}
        getKey={(r) => r.task_type}
        getLabel={(r) => t(`bruno.tasks_names.${resolveLegacyTaskType(r.task_type)}`, r.task_type)}
        onSelect={(r) => setCurrentTaskType(r.task_type)}
        progress={progressTasksType}
      />

      {/* ── Filters drawer ───────────────────────────────────────────────── */}
      <TasksFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        currentTaskType={currentTaskType}
        supplierSelected={supplierSelected}
        supplierIds={supplierIds}
        aislesByTaskList={aislesByTaskList}
        aisleClientNames={aisleClientNames}
        loadingAisles={loadingAisles}
        hasOverheadTasks={hasOverheadTasks}
        onApply={handleApplyFilters}
        onReset={resetFilter}
      />

      {/* ── Task detail modal ─────────────────────────────────────────────── */}
      <TasksDetailsContainer
        open={!!selectedTask}
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        tasksStatusOverride={tasksStatusList}
        contextRef={contextRef}
        resetPagination={resetPagination}
      />

      {/* ── Excel dialogs ─────────────────────────────────────────────────── */}
      <ExcelDialogContainer
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        currentTaskType={currentTaskType}
        onUploadSuccess={() => {
          resetPagination();
          clearTaskList();
          loadTaskList();
          getTaskList(0);
          getTasksProgress();
        }}
      />

    </Box>
  );
}
